import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import {
  AccountClient,
  HttpAccountService,
  RootIdType,
  TransactionTypeKey,
  checkSingleClient,
  displayClient,
} from '@helios/common-api'
import {
  RootState,
  selectActualReconDate,
  selectFocusedBulkCwTransactions,
  setFocusedBulkCwTransactions,
} from '@helios/state-management'
import { getZeroUtcDate, splitNumbers } from '@helios/utils'
import {
  AssetIdListValidator,
  dateDiffValidationMessages,
} from '@helios/validators'
import { Store } from '@ngrx/store'
import { DisplayDateFormat, toISOFormat } from '@northfork/basics'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import {
  BULK_CW_TRAN_LIMIT,
  BulkCwTranDateType,
  BulkCwTranTypeSetValue,
  FindBulkClearwaterTransactionsForm,
} from '../../models/bulk-clearwater-transactions.model'
import { BulkClearwaterTransactionsService } from '../../services/bulk-clearwater-transactions.service'

@Component({
  selector: 'hs-find-transactions-popover',
  templateUrl: './find-transactions-popover.component.html',
  styleUrls: ['./find-transactions-popover.component.scss'],
})
export class FindTransactionsPopoverComponent implements OnInit, AfterViewInit {
  BulkCwTranDateType = BulkCwTranDateType

  @Output() find = new EventEmitter<FindBulkClearwaterTransactionsForm>()
  @Output() getBulkTxByTranIds =
    new EventEmitter<FindBulkClearwaterTransactionsForm>()

  private readonly _destroyed$ = new Subject<void>()

  isFormSubmitting = false
  applyDisabled = false
  dateFormat = DisplayDateFormat
  dateRangeErrorMessages = { dateDiffValidationMessages }
  bulkCwTranTypes: BulkCwTranTypeSetValue[] = []
  ultimateParentId?: string
  hasSingleUltimateParent = false
  ultimateParentDisplay?: string
  accountIds: number[] = []
  ids: string[] = []
  bulkEditTranIds: number[] = []
  ignoreUltParentCheck = false
  transactionLimitBreached = false

  readonly dateRangeType = new FormControl('ENTRY', [Validators.required])
  readonly rootIdType = new FormControl('account', [Validators.required])
  readonly rootIds = new FormControl('', [Validators.required])
  readonly startDate = new FormControl(null, [Validators.required])
  readonly endDate = new FormControl(getZeroUtcDate(new Date().toISOString()), [
    Validators.required,
  ])
  readonly assetIds = new FormControl('', [AssetIdListValidator])
  readonly transactionTypes = new FormControl([])
  tranCountLimit = BULK_CW_TRAN_LIMIT

  form = new FormGroup({
    dateRangeType: this.dateRangeType,
    startDate: this.startDate,
    endDate: this.endDate,
    rootIdType: this.rootIdType,
    rootIds: this.rootIds,
    assetIds: this.assetIds,
    transactionType: this.transactionTypes,
  })

  constructor(
    private readonly store: Store<RootState>,
    private readonly bulkService: BulkClearwaterTransactionsService,
    private readonly httpAccountService: HttpAccountService,
  ) {}

  ngOnInit() {
    // Fetch transaction types available to this panel
    this.bulkService
      .getTransactionTypes()
      .pipe(take(1))
      .subscribe(tranTypes => {
        this.bulkCwTranTypes = tranTypes.sort((a, b) =>
          a.type.localeCompare(b.type),
        )
        this.selectAllForDropdownItems(this.bulkCwTranTypes)
      })

    this.store
      .select(selectActualReconDate)
      .pipe(take(1))
      .subscribe(date => {
        this.startDate.setValue(toISOFormat(date))
      })

    // get tx data when transaction breaks are selected from the breaks panel for 'Bulk Edit'
    this.store
      .select(selectFocusedBulkCwTransactions)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(result => {
        this.handleBulkEdit(result)
      })
  }

  ngAfterViewInit() {
    this.rootIds.valueChanges.subscribe(value => this.onRootIdsChange(value))
    this.rootIdType.valueChanges.subscribe(() =>
      this.onRootIdsChange(this.rootIds.value),
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectAllForDropdownItems(items: any[]) {
    items.forEach(element => {
      element['selectedAllGroup'] = 'selectedAllGroup'
    })
  }

  onSubmit() {
    this.find.emit({
      rootIdType: this.rootIdType.value,
      rootIds: this.rootIds.value,
      dateRangeType: this.dateRangeType.value,
      startDate: this.startDate.value,
      endDate: this.endDate.value,
      assetIds: this.assetIds.value,
      tranTypes: this.transactionTypes.value,
    })
    this.ignoreUltParentCheck = false
    this.hasSingleUltimateParent = true
  }

  onStartDateSelect() {
    this.endDate.updateValueAndValidity()
  }

  onEndDateSelect() {
    this.startDate.updateValueAndValidity()
  }

  trackTranTypeByType(
    _index: number,
    item: BulkCwTranTypeSetValue,
  ): TransactionTypeKey {
    return item.type
  }

  async onRootIdsChange(value: string) {
    this.transactionLimitBreached = false
    if (
      this.rootIdType.value === 'transaction' ||
      this.rootIdType.value === 'legacyTransaction'
    ) {
      this.hasSingleUltimateParent = true
      this.ignoreUltParentCheck = true
      this.startDate.disable()
      this.endDate.disable()
      const tranIds = splitNumbers(value)
      if (tranIds.length > this.tranCountLimit) {
        this.transactionLimitBreached = true
      }
      return
    }
    this.ignoreUltParentCheck = false
    const rootIds = splitNumbers(value)
    this.updateUltimateParent(rootIds)
    this.startDate.enable()
    this.endDate.enable()
  }

  async updateUltimateParent(rootIds: number[]) {
    const ultParents = await this.httpAccountService
      .getAccountUltimateParentClients(rootIds, this.rootIdType.value)
      .pipe(take(1))
      .toPromise()
    this.setUltimateParentClients(ultParents)
  }

  setUltimateParentClients(ultParents: AccountClient[]) {
    // Validation: IDs are within the same ultimate parent id
    this.hasSingleUltimateParent = checkSingleClient(ultParents)

    if (!this.hasSingleUltimateParent) {
      this.ultimateParentId = undefined
      this.ultimateParentDisplay = undefined
      return
    }

    const ultParent = ultParents[0]
    this.ultimateParentId = ultParent.id
    this.ultimateParentDisplay = displayClient(ultParent)
  }

  handleBulkEdit(tranIds: number[] | undefined) {
    if (!tranIds || tranIds.length === 0) return

    this.bulkEditTranIds = tranIds ? tranIds : []
    this.getBulkTxByTranIds.emit({
      rootIdType: RootIdType.legacyTransaction,
      rootIds: this.bulkEditTranIds.join(','),
    })

    this.updateTranIdFormValues(tranIds)
  }

  updateTranIdFormValues(tranIds: number[] | undefined) {
    this.form.patchValue({
      rootIdType: RootIdType.legacyTransaction,
      rootIds: tranIds?.join(','), // converting to a string for the ultParentId validation
    })

    // purging redux so that closing and reopening bulk cw panel won't have the previous transactions data loaded
    // and adding setTimeout to allow the bulk cw panel to load first with transactions before purging the redux
    setTimeout(() => {
      return this.store.dispatch(setFocusedBulkCwTransactions({ tranIds: [] }))
    }, 10)
    this.bulkEditTranIds = []
  }
}
